import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, ExternalLink, Eye, EyeOff, Search } from 'lucide-react';
import { toast } from 'sonner';

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI
      .listProperties()
      .then(setProperties)
      .catch(() => toast.error('Failed to load properties'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = properties.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.title || '').toLowerCase().includes(q);
  });

  const toggleLive = async (p) => {
    try {
      const res = await adminAPI.toggleLive(p.id);
      toast.success(res.is_live ? 'Property is now live' : 'Property is now offline');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await adminAPI.deleteProperty(toDelete.id);
      toast.success('Property deleted');
      setToDelete(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Properties</h1>
            <p className="text-gray-400 text-sm">{properties.length} total</p>
          </div>
          <Link to="/admin/properties/new">
            <Button className="bg-red-600 hover:bg-red-700" data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Property
            </Button>
          </Link>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-700 text-white"
            data-testid="admin-properties-search"
          />
        </div>

        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : filtered.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-8 text-center text-gray-400">
              {search ? 'No properties match your search.' : 'No properties yet. Add your first one!'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <Card key={p.id} className="bg-gray-800 border-gray-700 overflow-hidden">
                <div className="aspect-video bg-gray-900 relative">
                  {p.main_image ? (
                    <img src={p.main_image} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                      No image
                    </div>
                  )}
                  <span
                    className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-medium ${
                      p.is_live !== false
                        ? 'bg-green-500/20 text-green-300 border border-green-500/40'
                        : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                    }`}
                  >
                    {p.is_live !== false ? 'Live' : 'Offline'}
                  </span>
                </div>

                <CardContent className="p-4 space-y-3">
                  <h3 className="font-semibold text-white line-clamp-1">{p.title}</h3>

                  {p.airbnb_url ? (
                    <a
                      href={p.airbnb_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-red-400 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> View on airbnb
                    </a>
                  ) : (
                    <p className="text-xs text-yellow-500">⚠ No airbnb URL set</p>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Link to={`/admin/properties/${p.id}/edit`}>
                      <Button variant="outline" size="sm" className="border-gray-600 text-gray-200 hover:bg-gray-700">
                        <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-gray-600 text-gray-200 hover:bg-gray-700"
                      onClick={() => toggleLive(p)}
                      data-testid={`toggle-live-${p.id}`}
                    >
                      {p.is_live !== false ? (
                        <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</>
                      ) : (
                        <><Eye className="w-3.5 h-3.5 mr-1" /> Publish</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                      onClick={() => setToDelete(p)}
                      data-testid={`delete-property-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete property?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Permanently delete <span className="text-white font-semibold">{toDelete?.title}</span>.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="border-gray-600 bg-gray-800 text-white">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminProperties;
